import { ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MediaField from "@/components/admin/MediaField";
import AiToolbar from "@/components/admin/AiToolbar";
import { FOCAL_OPTIONS, SCRIM_OPTIONS } from "@/lib/heroMedia";
import YouTubeTester from "@/components/admin/YouTubeTester";

const KINDS = [
  { value: "image", label: "Photo" },
  { value: "video", label: "Uploaded video" },
  { value: "youtube", label: "YouTube link" },
];

const emptySlide = () => ({
  id: `slide-${Date.now()}`,
  kind: "image",
  image_url: "",
  video_url: "",
  youtube_url: "",
  yt_start: 0,
  yt_end: 0,
  poster_url: "",
  image_url_mobile: "",
  focal: "center",
  focal_mobile: "center",
  scrim: "default",
  duration: 8,
  eyebrow: { en: "", ar: "" },
  lines: [],
  tagline: { en: "", ar: "" },
  highlights: [],
});

function LocPair({ label, value, onChange, testKey, index, area = false }) {
  const v = value || { en: "", ar: "" };
  const Tag = area ? "textarea" : "input";
  const cls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring";
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1.5 grid grid-cols-2 gap-2">
        <Tag
          rows={area ? 3 : undefined}
          className={cls}
          value={v.en}
          placeholder="English"
          data-testid={`field-hero_slides-${testKey}-en-${index}`}
          onChange={(e) => onChange({ ...v, en: e.target.value })}
        />
        <Tag
          rows={area ? 3 : undefined}
          dir="rtl"
          className={cls}
          value={v.ar}
          placeholder="العربية"
          data-testid={`field-hero_slides-${testKey}-ar-${index}`}
          onChange={(e) => onChange({ ...v, ar: e.target.value })}
        />
      </div>
      <AiToolbar
        fieldLabel={`Hero slide — ${label}`}
        value={v}
        onChange={onChange}
        testKey={`hero_slides-${testKey}-${index}`}
        allowExpand={!!area}
      />
    </div>
  );
}

function ListEditor({ label, items, onChange, testKey, index, withValue = false }) {
  const arr = Array.isArray(items) ? items : [];
  const blank = withValue ? { value: "", label: { en: "", ar: "" } } : { en: "", ar: "" };
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <button
          type="button"
          data-testid={`field-hero_slides-${testKey}-add-${index}`}
          onClick={() => onChange([...arr, blank])}
          className="text-xs font-semibold text-primary hover:underline"
        >
          + Add
        </button>
      </div>
      <div className="mt-2 space-y-2">
        {arr.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            {withValue && (
              <Input
                className="w-28 shrink-0"
                value={item?.value || ""}
                placeholder="280M"
                data-testid={`field-hero_slides-${testKey}-value-${index}-${i}`}
                onChange={(e) => {
                  const c = [...arr];
                  c[i] = { ...item, value: e.target.value };
                  onChange(c);
                }}
              />
            )}
            <Input
              value={(withValue ? item?.label?.en : item?.en) || ""}
              placeholder="English"
              data-testid={`field-hero_slides-${testKey}-en-${index}-${i}`}
              onChange={(e) => {
                const c = [...arr];
                c[i] = withValue
                  ? { ...item, label: { ...(item?.label || {}), en: e.target.value } }
                  : { ...item, en: e.target.value };
                onChange(c);
              }}
            />
            <Input
              dir="rtl"
              value={(withValue ? item?.label?.ar : item?.ar) || ""}
              placeholder="العربية"
              data-testid={`field-hero_slides-${testKey}-ar-${index}-${i}`}
              onChange={(e) => {
                const c = [...arr];
                c[i] = withValue
                  ? { ...item, label: { ...(item?.label || {}), ar: e.target.value } }
                  : { ...item, ar: e.target.value };
                onChange(c);
              }}
            />
            <button
              type="button"
              data-testid={`field-hero_slides-${testKey}-remove-${index}-${i}`}
              onClick={() => onChange(arr.filter((_, x) => x !== i))}
              className="px-2 text-xs text-destructive hover:underline"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SlideListField({ label, value, onChange }) {
  const slides = Array.isArray(value) ? value : [];

  const patch = (i, part) => {
    const c = [...slides];
    c[i] = { ...(slides[i] || {}), ...part };
    onChange(c);
  };
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= slides.length) return;
    const c = [...slides];
    [c[i], c[j]] = [c[j], c[i]];
    onChange(c);
  };

  return (
    <div className="md:col-span-2">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Slides rotate in order. Leave the text fields blank to reuse the main hero headline below.
          </p>
        </div>
        <button
          type="button"
          data-testid="field-hero_slides-add"
          onClick={() => onChange([...slides, emptySlide()])}
          className="btn-outline h-9 px-4 text-xs"
        >
          + Add slide
        </button>
      </div>

      <div className="mt-4 space-y-4">
        {slides.map((slide, i) => (
          <div
            key={slide.id || i}
            data-testid={`hero-slide-row-${i}`}
            className="space-y-4 border border-border p-4"
            style={{ borderRadius: "var(--radius)" }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-head text-xl text-primary">{String(i + 1).padStart(2, "0")}</span>
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
                  value={slide.kind || "image"}
                  data-testid={`hero-slide-kind-${i}`}
                  onChange={(e) => patch(i, { kind: e.target.value })}
                >
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`hero-slide-up-${i}`} onClick={() => move(i, -1)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-foreground/70 transition-colors hover:border-primary hover:text-primary">
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" data-testid={`hero-slide-down-${i}`} onClick={() => move(i, 1)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-foreground/70 transition-colors hover:border-primary hover:text-primary">
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  data-testid={`hero-slide-remove-${i}`}
                  onClick={() => onChange(slides.filter((_, x) => x !== i))}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-destructive transition-colors hover:border-primary hover:text-primary hover:border-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {slide.kind === "image" && (
              <MediaField label="Photo" accept="image" testKey={`hero_slides-image_url-${i}`} value={slide.image_url} onChange={(v) => patch(i, { image_url: v })} />
            )}
            {slide.kind === "video" && (
              <MediaField label="Video file" accept="video" testKey={`hero_slides-video_url-${i}`} value={slide.video_url} onChange={(v) => patch(i, { video_url: v })} />
            )}
            {slide.kind === "youtube" && (
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs text-muted-foreground">YouTube link</Label>
                  <Input
                    className="mt-1.5"
                    value={slide.youtube_url || ""}
                    placeholder="https://youtu.be/..."
                    data-testid={`hero-slide-youtube-${i}`}
                    onChange={(e) => patch(i, { youtube_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Start (seconds)</Label>
                  <Input
                    className="mt-1.5"
                    type="number"
                    min={0}
                    value={slide.yt_start ?? 0}
                    data-testid={`hero-slide-yt-start-${i}`}
                    onChange={(e) => patch(i, { yt_start: Number(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End (seconds)</Label>
                  <Input
                    className="mt-1.5"
                    type="number"
                    min={0}
                    value={slide.yt_end ?? 0}
                    data-testid={`hero-slide-yt-end-${i}`}
                    onChange={(e) => patch(i, { yt_end: Number(e.target.value) || 0 })}
                  />
                </div>
                <div className="sm:col-span-4">
                  <YouTubeTester
                    url={slide.youtube_url}
                    start={slide.yt_start || 0}
                    end={slide.yt_end || 0}
                    testId={`hero-slide-yt-test-${i}`}
                  />
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Leave End at 0 to play the whole video. The clip is muted and loops back to Start automatically.
                    If a video cannot be embedded, the hero falls back to the poster image below.
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <MediaField
                  label={slide.kind === "image" ? "Fallback image (optional)" : "Poster / fallback image"}
                  accept="image"
                  compact
                  testKey={`hero_slides-poster_url-${i}`}
                  value={slide.poster_url}
                  onChange={(v) => patch(i, { poster_url: v })}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Hold for (seconds)</Label>
                <Input
                  className="mt-1.5"
                  type="number"
                  min={2}
                  value={slide.duration ?? 8}
                  data-testid={`hero-slide-duration-${i}`}
                  onChange={(e) => patch(i, { duration: Number(e.target.value) || 8 })}
                />
              </div>
            </div>

            <div className="grid gap-3 border-t border-border pt-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Desktop focus point</Label>
                <select
                  className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
                  value={slide.focal || "center"}
                  data-testid={`hero-slide-focal-${i}`}
                  onChange={(e) => patch(i, { focal: e.target.value })}
                >
                  {FOCAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Mobile focus point</Label>
                <select
                  className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
                  value={slide.focal_mobile || "center"}
                  data-testid={`hero-slide-focal-mobile-${i}`}
                  onChange={(e) => patch(i, { focal_mobile: e.target.value })}
                >
                  {FOCAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Text legibility shading</Label>
                <select
                  className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
                  value={slide.scrim || "default"}
                  data-testid={`hero-slide-scrim-${i}`}
                  onChange={(e) => patch(i, { scrim: e.target.value })}
                >
                  {SCRIM_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <MediaField
              label="Separate mobile image (optional — use a tighter crop for phones)"
              accept="image"
              testKey={`hero_slides-image_url_mobile-${i}`}
              value={slide.image_url_mobile}
              onChange={(v) => patch(i, { image_url_mobile: v })}
            />

            <LocPair label="Eyebrow line" index={i} testKey="eyebrow" value={slide.eyebrow} onChange={(v) => patch(i, { eyebrow: v })} />
            <ListEditor label="Headline lines" index={i} testKey="lines" items={slide.lines} onChange={(v) => patch(i, { lines: v })} />
            <LocPair area label="Sub-line" index={i} testKey="tagline" value={slide.tagline} onChange={(v) => patch(i, { tagline: v })} />
            <ListEditor withValue label="Highlight numbers" index={i} testKey="highlights" items={slide.highlights} onChange={(v) => patch(i, { highlights: v })} />
          </div>
        ))}
      </div>
    </div>
  );
}
